import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiAcceptInvite, apiGetInvitePreview, setAuthSession } from '../api/client';

/**
 * Invitation link from email: /accept-invite?token=…
 */
export default function AcceptInvitePage({ language, setLanguage, supportedLocales = [], t }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (!token) {
          setPreview({ valid: false });
          return;
        }
        const data = await apiGetInvitePreview(token);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) setPreview({ valid: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isExistingInvite =
    !!(preview?.existingAccount ?? preview?.ExistingAccount);

  const submit = async (event) => {
    event.preventDefault();
    if (!password || (!isExistingInvite && password.length < 8)) {
      setError(
        isExistingInvite ? t('inviteEnterPassword') : t('passwordMinLength'),
      );
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const data = await apiAcceptInvite({ token, password });
      const tokenEmail = typeof data.email === 'string' ? data.email : '';
      const authToken =
        typeof data.token === 'string' ? data.token : data.Token ?? '';
      if (!authToken || !tokenEmail)
        throw new Error(t('inviteIncompleteResponse'));

      setAuthSession({ token: authToken, email: tokenEmail });
      window.location.replace('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('inviteSetupFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const invalidCopy = loading
    ? t('checkingInviteLink')
    : t('invalidOrExpiredInvite');

  const roleLabel =
    preview?.role ?? preview?.Role ?? (preview?.valid || preview?.Valid ? '' : '');
  const emailHint =
    preview?.emailHint ?? preview?.EmailHint ?? '';

  return (
    <div className="min-h-screen bg-[#f4f6f8] px-4 py-10 dark:bg-[#111111]">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-brand-200 bg-white p-6 shadow-xl dark:border-brand-900 dark:bg-slate-900">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
          {loading
            ? '…'
            : preview?.valid || preview?.Valid
              ? isExistingInvite
                ? t('acceptInviteExistingTitle')
                : t('setYourPasswordTitle')
              : t('inviteLinkProblemTitle')}
        </h1>

        {(preview?.valid || preview?.Valid) && !loading && (
          <>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {emailHint}{' '}
              {roleLabel && (
                <span className="block pt-2">
                  {t('inviteRoleLabel')}: <strong>{roleLabel}</strong>
                </span>
              )}
            </p>

            {isExistingInvite && (
              <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {t('acceptInviteExistingBlurb')}
              </p>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block text-xs">
                <span className="mb-1 block text-slate-500 dark:text-slate-300">
                  {isExistingInvite ? t('invitePasswordLabelExisting') : t('invitePasswordLabelNew')}
                </span>
                <input
                  type="password"
                  value={password}
                  disabled={submitting}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={isExistingInvite ? 'current-password' : 'new-password'}
                  className="w-full rounded-xl border border-brand-200 bg-white px-3 py-2 text-sm outline-none ring-brand-300 focus:ring disabled:opacity-70 dark:border-brand-900 dark:bg-slate-950"
                  placeholder=""
                />
              </label>

              {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? isExistingInvite
                    ? t('joinWorkspaceSubmitting')
                    : t('creatingAccount')
                  : isExistingInvite
                    ? t('joinWorkspaceButton')
                    : t('finishAndSignIn')}
              </button>
            </form>
          </>
        )}

        {preview && !(preview.valid || preview.Valid) && !loading && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">{invalidCopy}</p>
            <Link
              to="/login"
              className="mt-4 inline-block rounded-lg border border-brand-300 px-4 py-2 text-sm font-medium text-brand-800 dark:border-brand-700 dark:text-brand-200"
            >
              {t('loginTitle')}
            </Link>
          </div>
        )}

        <div className="mt-8 border-t border-brand-200/70 pt-4 dark:border-brand-900">
          <label className="flex items-center justify-between gap-2 text-xs text-slate-600 dark:text-slate-400">
            {t('language')}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="rounded-lg border border-brand-200 bg-white px-2 py-1 dark:border-brand-900 dark:bg-slate-950"
            >
              {supportedLocales.map((locale) => (
                <option key={locale.code} value={locale.code}>
                  {locale.nativeLabel}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
